import React from 'react';
import CartWrapStyleStyle from './style';
import { useSelector } from 'react-redux';
import { quoteDetailSelector } from 'src/redux/selectors/quoteDetailSelector';
import { formatCurrency } from 'src/const/contain';
import CartItem from '../cartItem';
import moment from 'moment';
import { Flex } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom';
import { DATE_FORMAT } from 'src/const/common';

interface CartWrapProps {
    isMiniCart?: boolean;
}

const CartWrap: React.FC<CartWrapProps> = ({ isMiniCart = true }) => {
    const location = useLocation();
    const {
        price: priceTotal,
        groups,
        tourName,
        isEvent,
        extraYards = [],
        teeTimes = [],
    } = useSelector(quoteDetailSelector);

    const resultGroups =
        groups && typeof groups === 'object'
            ? Object?.entries(groups)
                  ?.map(([group, items]) => ({
                      group,
                      items,
                  }))
                  .sort((a, b) => a.group.localeCompare(b.group))
            : [];

    const {
        totalPrice,
        totalDeposit,
        dueDate,
        deposit2,
        totalSavings,
    } = priceTotal;

    const { amount, dueDate: deposit2DueDate } = deposit2;

    const isCheckPriceAndDeposit = !!(
        Number(totalPrice) === Number(totalDeposit)
    );

    const formatDate = (date: string) => {
        if (date) {
            return moment(date, DATE_FORMAT).format(DATE_FORMAT);
        }
        return;
    };

    const formatTime = (time?: string) => {
        const utcTime = moment.utc(time);
        const formattedTime = utcTime.format('HH:mm');
        if (formattedTime === '00:00') {
            return ''; //Time TBC
        }
        return utcTime.format('HH:mm');
    };

    const renderCarts = () => (
        <>
            {resultGroups.map((item, index) => (
                <CartItem
                    key={index}
                    item={item}
                    numOfGroups={resultGroups.length}
                    tourName={tourName}
                    isMiniCart={isMiniCart}
                />
            ))}
        </>
    );

    const renderTotal = () => (
        <div className="box-total">
            <div className="item total">
                <div className="name">Total Price</div>
                <div className="value">£{formatCurrency(totalPrice)}</div>
            </div>

            {Number(totalSavings) > 0 && (
                <div className="item savings">
                    <div className="name">
                        Total Savings (applied)
                    </div>
                    <div className="value">
                        -£{formatCurrency(totalSavings)}
                    </div>
                </div>
            )}

            {!isCheckPriceAndDeposit && (
                <div className="item">
                    <div className="name">Deposit due now</div>
                    <div className="value">£{formatCurrency(totalDeposit)}</div>
                </div>
            )}
            {!isEvent && Number(amount) > 0 && (
                <div className="item">
                    <div className="name">
                        Deposit due {formatDate(deposit2DueDate)}
                    </div>
                    <div className="value">£{formatCurrency(amount)}</div>
                </div>
            )}

            {!isCheckPriceAndDeposit && (
                <div className="item">
                    <div className="name">Balance Due Date</div>
                    <div className="value">{formatDate(dueDate)}</div>
                </div>
            )}
        </div>
    );

    const renderExtraYards = () => (
        <>
            {extraYards.length > 0 && (
                <div className="extra-yards">
                    <div className="sub-title">Extra Yards</div>
                    {extraYards.map((item: string, index: number) => (
                        <Flex
                            align="center"
                            key={index}
                            gap={8}
                            className="item"
                        >
                            <FontAwesomeIcon
                                className="icon-ex"
                                icon={faCheck}
                            />
                            <span>{item}</span>
                        </Flex>
                    ))}
                </div>
            )}
        </>
    );

    const renderTeeTimes = () => (
        <>
            {teeTimes.length > 0 && (
                <div className="tee-times">
                    <div className="sub-title">Your Tee Times</div>
                    {teeTimes.map((item: any, index: number) => (
                        <div className="item" key={index}>
                            {item?.rounds} Round
                            {Number(item?.rounds) > 1 ? 's' : ''} of Golf at{' '}
                            {item?.courseName} - {item?.startAtTime}{' '}
                            on{' '}
                            {moment(item?.startTime || 0)
                                .utc()
                                .format(DATE_FORMAT)}
                        </div>
                    ))}
                </div>
            )}
        </>
    );

    return (
        <CartWrapStyleStyle className={isMiniCart ? 'mini-cart' : ''}>
            {renderCarts()}
            {isMiniCart &&
                !location.pathname.includes('pricingAndBooking') &&
                renderTeeTimes()}
            {renderExtraYards()}
            {renderTotal()}
        </CartWrapStyleStyle>
    );
};

export default CartWrap;
